/**
 * Dentis Cart Sync Manager
 * Centralizes cart refresh coordination across all Rebuy widgets
 * 
 * Solves timing issues when multiple widgets (addon, free gift) 
 * modify the cart in quick succession.
 * 
 * Strategy: Self-resetting debounce
 * - Each new cart event resets a short timer
 * - Refresh only happens after activity settles
 * - Single refresh includes all changes (adds AND removes)
 * - Blocks events only during initial page load
 * 
 * Auto-Remove Protection:
 * - Removes free brush heads (price=0) from cart if D2 Pro is not present
 * - Prevents abuse of free add-on promotions
 * 
 * Usage:
 *   - Listens for: rebuy:cart.change events
 *   - Directly refreshes cart drawer and icon
 */

(function() {
  'use strict';

  const DEBOUNCE_MS = 100; // Wait for activity to settle (resets on each new event)
  const INIT_BLOCK_MS = 2000; // Block events during initial page load
  const DEBUG = true; // Enable console logging

  // D2 Pro Toothbrush Variant IDs (main products that qualify for free brush heads)
  const D2_PRO_VARIANT_IDS = [
    50930393383206, // Musta (Black)
    50930393415974, // Valkoinen (White)
    50930393448742  // Duopaketti (Duo)
  ];

  // Brush Head Variant IDs (free add-ons that require D2 Pro in cart)
  const BRUSHHEAD_VARIANT_IDS = [
    50930393022758, // Musta (Black 4pk)
    50930394071334, // Valkoinen (White 4pk)
    50930393153830  // Musta ja valkoinen (Duo 8pk)
  ];

  function log(...args) {
    if (DEBUG) console.log('[DentisCartSync]', ...args);
  }

  // State
  let debounceTimer = null;
  let isRefreshing = false;
  let isInitialized = false; // Blocks events during page load
  let isRemovingItems = false; // Prevents re-entry during item removal
  let lastRemovalTime = 0; // Timestamp of last removal for cooldown
  const REMOVAL_COOLDOWN_MS = 1500; // Skip orphan checks for this long after removal

  /**
   * Notify that a cart change has occurred
   * Each call resets the debounce timer - refresh only happens
   * after no new events for DEBOUNCE_MS
   */
  function notifyCartChange() {
    log('📦 notifyCartChange() - initialized:', isInitialized);
    
    // Block events during initial page load to prevent unwanted refreshes
    if (!isInitialized) {
      log('⏸️ Blocked - still in init period');
      return;
    }
    
    // Clear any pending debounce - this is the key to "wait for settle"
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      log('🔄 Reset debounce timer');
    }

    // Schedule refresh after activity settles
    debounceTimer = setTimeout(() => {
      log('⏱️ Activity settled, executing refresh...');
      refreshCart();
    }, DEBOUNCE_MS);
    
    log('⏳ Will refresh in', DEBOUNCE_MS, 'ms (unless new event)');
  }

  /**
   * Check cart for orphaned free brush heads (brush heads with price=0 but no D2 Pro in cart)
   * and remove them automatically to prevent abuse.
   * 
   * @returns {Promise<boolean>} - true if items were removed, false otherwise
   */
  async function checkAndRemoveOrphanedBrushHeads() {
    if (isRemovingItems) {
      log('⚠️ Already removing items, skipping check');
      return false;
    }

    // Cooldown check - skip if we recently removed items to let cart settle
    const timeSinceRemoval = Date.now() - lastRemovalTime;
    if (lastRemovalTime > 0 && timeSinceRemoval < REMOVAL_COOLDOWN_MS) {
      log('⏳ In removal cooldown (' + Math.round((REMOVAL_COOLDOWN_MS - timeSinceRemoval) / 1000 * 10) / 10 + 's remaining), skipping check');
      return false;
    }

    try {
      log('🔍 Checking for orphaned free brush heads...');
      
      // Fetch current cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (!cart.items || cart.items.length === 0) {
        log('🛒 Cart is empty, no check needed');
        return false;
      }

      // Check if any D2 Pro variant is in the cart
      const hasD2Pro = cart.items.some(item => D2_PRO_VARIANT_IDS.includes(item.variant_id));
      log('🦷 D2 Pro in cart:', hasD2Pro);

      if (hasD2Pro) {
        log('✅ D2 Pro present, free brush heads are valid');
        return false;
      }

      // Find free brush heads (price = 0) that need to be removed
      // Using final_line_price which accounts for discounts
      const freeBrushHeadsToRemove = cart.items.filter(item => {
        const isBrushHead = BRUSHHEAD_VARIANT_IDS.includes(item.variant_id);
        const isFree = item.final_line_price === 0;
        
        if (isBrushHead) {
          log(`  🪥 Brush head found: variant ${item.variant_id}, final_line_price: ${item.final_line_price}, isFree: ${isFree}`);
        }
        
        return isBrushHead && isFree;
      });

      if (freeBrushHeadsToRemove.length === 0) {
        log('✅ No orphaned free brush heads found');
        return false;
      }

      log('⚠️ Found', freeBrushHeadsToRemove.length, 'orphaned free brush head(s), removing...');
      isRemovingItems = true;

      // Build updates object to set quantities to 0 for these line items
      const updates = {};
      freeBrushHeadsToRemove.forEach(item => {
        // Use the line item key for removal
        updates[item.key] = 0;
        log('  🗑️ Marking for removal:', item.title, '(key:', item.key, ')');
      });

      // Remove items via Shopify cart update API
      const removeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove items from cart');
      }

      log('✅ Orphaned free brush heads removed successfully');
      isRemovingItems = false;
      lastRemovalTime = Date.now(); // Start cooldown
      log('⏳ Starting removal cooldown for', REMOVAL_COOLDOWN_MS, 'ms');
      return true;

    } catch (error) {
      log('❌ Error checking/removing orphaned brush heads:', error);
      isRemovingItems = false;
      return false;
    }
  }

  /**
   * Parse section HTML the same way the theme does.
   */
  function getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  /**
   * Fetch cart sections and update the DOM using the theme's section strategy,
   * but WITHOUT calling cartDrawer.open() (least moving parts, no recursion).
   * 
   * @param {boolean} skipOrphanCheck - If true, skip the orphan brush head check (used after auto-removal)
   */
  function refreshCart(skipOrphanCheck = false) {
    log('🔄 refreshCart() - isRefreshing:', isRefreshing, 'skipOrphanCheck:', skipOrphanCheck);

    // Prevent concurrent refreshes
    if (isRefreshing) {
      log('⚠️ Already refreshing, skipping');
      return;
    }

    isRefreshing = true;
    log('🚀 Fetching cart sections...');

    const url = new URL(window.location.href);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');

    fetch(url.toString(), { method: 'GET' })
      .then(response => response.json())
      .then(async sections => {
        log('✓ Got sections, updating DOM');

        // Update cart drawer section content (matches cart-drawer.js logic)
        const cartDrawerRoot = document.querySelector('#CartDrawer');
        const cartDrawerHtml = getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
        if (cartDrawerRoot && cartDrawerHtml !== null) {
          cartDrawerRoot.innerHTML = cartDrawerHtml;
        }

        // Update cart icon bubble innerHTML (matches cart-drawer.js default selector behavior)
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        const cartIconHtml = getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
        if (cartIconBubble && cartIconHtml !== null) {
          cartIconBubble.innerHTML = cartIconHtml;
        }

        // Re-bind overlay click handler if overlay exists (renderContents does this)
        const cartDrawerEl = document.querySelector('cart-drawer');
        const overlay = cartDrawerEl ? cartDrawerEl.querySelector('#CartDrawer-Overlay') : null;
        if (cartDrawerEl && overlay && typeof cartDrawerEl.close === 'function') {
          overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));
        }

        isRefreshing = false;
        log('✓ Cart refresh complete');

        // Check for orphaned free brush heads
        // Note: If items are removed, Rebuy will fire rebuy:cart.change which triggers another refresh
        // The cooldown mechanism prevents cascading checks
        if (!skipOrphanCheck) {
          await checkAndRemoveOrphanedBrushHeads();
          // No manual refresh needed - rebuy:cart.change event handles it
        }
      })
      .catch(error => {
        log('❌ Error refreshing cart:', error);
        isRefreshing = false;
      });
  }

  /**
   * Force an immediate refresh (bypasses debounce and init check)
   */
  function forceRefresh() {
    log('⚡ forceRefresh() called');
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    refreshCart();
  }

  // Listen for Rebuy's native cart change event
  document.addEventListener('rebuy:cart.change', function() {
    log('📣 rebuy:cart.change event received');
    notifyCartChange();
  });

  // Initialize after page load settles
  function init() {
    log('📄 Initializing, blocking events for', INIT_BLOCK_MS, 'ms');
    
    setTimeout(() => {
      isInitialized = true;
      log('✓ Ready - now accepting cart change events');
    }, INIT_BLOCK_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API
  window.DentisCartSync = {
    notifyCartChange: notifyCartChange,
    forceRefresh: forceRefresh,
    checkOrphanedBrushHeads: checkAndRemoveOrphanedBrushHeads
  };

  log('✓ Script loaded');

})();
